/**
 * Visual tuning for Jump mode rendering. All values are in CSS px unless
 * otherwise noted; the renderer applies the layout's playfield-to-canvas scale
 * before drawing so identical visuals appear at any window size.
 */

/** Obstacles further than this many beats away aren't drawn (offscreen + slop). */
export const OBSTACLE_DRAW_BEATS_AHEAD = 13;
/** Obstacles already passed this many beats ago stop drawing (they've scrolled off-left). */
export const OBSTACLE_DRAW_BEATS_BEHIND = 3;

/** Width of a single spike's base, in playfield units. */
export const SPIKE_BASE_WIDTH = 30;

/**
 * Terrain rendering. Raised platforms and pit cliffs expose a lethal vertical
 * leading face (the engine's block-face hazard); a hazard-coloured stripe of
 * this width (playfield units) marks it so "don't slam the wall" reads. A pit's
 * near drop-off gets a dimmer danger edge of the same width.
 */
export const TERRAIN_FACE_ACCENT_WIDTH = 5;
/** Alpha of a lethal step-up / cliff face stripe vs a (safe-to-fall) pit drop-off edge. */
export const TERRAIN_FACE_LETHAL_ALPHA = 0.85;
export const TERRAIN_DROP_EDGE_ALPHA = 0.5;

/** Sawblade spin rate (radians per beat) — render-only, deterministic with the scroll. */
export const SAW_SPIN_RAD_PER_BEAT = 2.4;
/** Sawblade tooth count and the tip-radius multiple over the body radius. */
export const SAW_TOOTH_COUNT = 9;
export const SAW_TOOTH_TIP_MUL = 1.32;

/**
 * Ceiling (overhang) visuals. The slab hangs from the top of the playfield down
 * to its lethal underside; a bright hazard-coloured lip + downward stalactite
 * teeth mark the kill edge so it reads as "duck under me" at a glance — the
 * mirror of a spike row. Slab fill is dim so the underside lip dominates.
 */
export const CEILING_FILL_ALPHA = 0.9;
export const CEILING_LIP_HEIGHT = 5;
/** Stalactite tooth pitch + drop (playfield units) along the underside lip. */
export const CEILING_TOOTH_SPACING = 26;
export const CEILING_TOOTH_DROP = 11;

/**
 * Grid coaching-pill label ({@link import('@shared/jumpGrid').JumpGridHint}).
 * Drawn as a small pill above the surface at a hinted column in the tutorial to
 * coach "TAP" vs "HOLD" vs "SHORT!". Reference px (scaled by the layout); fades
 * with the same approach proximity as the telegraph so it arrives on cue.
 */
export const OBSTACLE_HINT_FONT_PX = 17;
export const OBSTACLE_HINT_GAP = 26;
export const OBSTACLE_HINT_MAX_ALPHA = 0.95;
/** Beats out from the cube column at which a hint starts fading in. */
export const OBSTACLE_HINT_FADE_BEATS = 5;

/** Player trail sample count (one per frame, fades by index). */
export const PLAYER_TRAIL_LENGTH = 20;
export const PLAYER_TRAIL_LENGTH_LOW_TIER = 10;

/** Parallax star count when decorations are enabled (high tier). */
export const STAR_COUNT = 50;
/** Parallax star drift as a fraction of canvas width per beat (beat-locked forward motion). */
export const STAR_DRIFT_FRAC_PER_BEAT = 0.08;

/** Cap active shockwave rings — bounds per-frame stroke + shadow work. */
export const SHOCKWAVE_POOL_MAX = 32;
/**
 * Tighter cap on the low (mobile) tier. Auto-hop pushes a land ripple on every
 * touchdown, so during fast hold sections the ring stack fills quickly; a lower
 * ceiling bounds the per-frame large-ellipse stroke count without the player
 * noticing fewer faint, short-lived ripples.
 */
export const SHOCKWAVE_POOL_MAX_LOW = 12;

/** Cube spin speed while airborne, in radians per second (Geometry-Dash quarter-turn tumble). */
export const CUBE_SPIN_RAD_PER_SEC = 7.2;
/** How fast a grounded cube eases back to a flat (multiple-of-90°) rotation. */
export const CUBE_SNAP_RATE = 22;

/** Star/cup milestone banner colour. */
export const MILESTONE_COLOUR = '#ffd84d';

/** Death effect colour (red — shared vocabulary across modes). */
export const DEATH_COLOUR = '#ff3b5c';

/** Verdict colours for floating score pops. */
export const VERDICT_COLOURS = {
  perfect: '#3bff9d',
  good: '#16e0ff',
  miss: '#ff3b5c',
} as const;

/**
 * Perfect-zone overlay. A translucent band floated above each LONE spike / saw
 * marking the {@link import('@shared/jumpChart').JUMP_PERFECT_MIN_CLEARANCE}…MAX
 * clearance window the cube's underside must thread for a `perfect` — the visual
 * twin of the grading band, so "fly through the green" reads literally. Fades in
 * over the approach (like the telegraph) and breathes on the beat; skipped for
 * clusters, whose width forces the full jump so there's no perfect window to aim
 * at. Drawn from the obstacle top up by [MIN, MAX] clearance. Cosmetic only.
 */
export const PERFECT_ZONE_COLOUR = VERDICT_COLOURS.perfect;
/** Beats out from the cube's column at which the zone starts fading in. */
export const PERFECT_ZONE_FADE_BEATS = 4.5;
/** Band fill + edge-rail alpha at closest approach. */
export const PERFECT_ZONE_FILL_ALPHA = 0.16;
export const PERFECT_ZONE_EDGE_ALPHA = 0.5;
/** Fraction of the alpha that breathes with the beat pulse. */
export const PERFECT_ZONE_PULSE_DEPTH = 0.35;
/** Half-width of the band in playfield units (a touch wider than the cube). */
export const PERFECT_ZONE_HALF_WIDTH = 20;

/** Floating-text lifetimes in seconds. */
export const FLOATING_TEXT_LIFE = 0.55;
export const FLOATING_TEXT_LIFE_BIG = 1.1;
export const SCORE_POP_LIFE = 0.75;

/** Vertical anchor for big celebration banners, as a fraction of canvas height (top-centre headline). */
export const BIG_BANNER_Y_FRAC = 0.16;

/** Verdict floating-text anchor offsets (CSS px) relative to the cube — above it, clear of the lane. */
export const VERDICT_TEXT_X_OFFSET = 0;
export const VERDICT_TEXT_Y_OFFSET = 64;

/** Top-left score HUD position (CSS px from canvas top-left corner). */
export const SCORE_HUD_X = 24;
export const SCORE_HUD_Y = 18;

/** Combo banner thresholds — passing one of these obstacle counts without dying triggers a banner. */
export const COMBO_MILESTONES = [10, 25, 50, 100] as const;

/** Star particle burst counts per milestone tier. */
export const MILESTONE_BURST_COUNT = [40, 60, 90, 120, 150, 200] as const;

/** Default key label rendered in the HUD's "Tap X to jump" hint. */
export const DEFAULT_KEY_LABEL = 'SPACE';

/**
 * Vertical clearance (playfield units the cube's bottom cleared the obstacle's
 * top) below which a pass is shown as a "close call" — bright accent flourish +
 * "CLOSE!" pop. Pure cosmetic: no scoring or replay-determinism impact.
 */
export const CLOSE_CALL_CLEARANCE = 12;

/** Hot-pink accent used for close-call FX. */
export const CLOSE_CALL_COLOUR = '#ff2bd6';

/** Beat-sync pulse strength for background / ground / cube glow. */
export const BEAT_PULSE_STRENGTH = 0.38;

/**
 * Hit-stop durations (ms) — render freeze only, engine/audio unaffected.
 * PERFECT freezes noticeably longer than GOOD so a clean clear lands with more
 * weight than a scrappy one; CLOSE / DEATH escalate from there.
 */
export const HIT_STOP_MS_PASS_PERFECT = 46;
export const HIT_STOP_MS_PASS_GOOD = 22;
export const HIT_STOP_MS_CLOSE = 48;
export const HIT_STOP_MS_DEATH = 95;
export const HIT_STOP_MAX_MS = 120;

/**
 * Per-verdict obstacle-pass feel dials. PERFECT is tuned to feel clearly more
 * satisfying than GOOD: a brighter edge flash, a small screen punch, a denser
 * spark spray, and (in the session) a second expanding shockwave ring.
 */
export const PASS_FLASH_INTENSITY_PERFECT = 0.72;
export const PASS_FLASH_INTENSITY_GOOD = 0.4;
/** Screen-shake amount on a PERFECT clear (GOOD gets none — it stays calm). */
export const PASS_SHAKE_PERFECT = 2.6;
/** Particle-burst strength multipliers per verdict (scales the obstacle_pass spark count). */
export const PASS_PARTICLE_STRENGTH_PERFECT = 1.6;
export const PASS_PARTICLE_STRENGTH_GOOD = 0.85;

/**
 * Tiny downward screen punch on each landing. Kept subtle because auto-hop
 * lands frequently — it should read as a grounded "thud", never a rumble.
 */
export const LAND_SHAKE_AMOUNT = 1.8;

/**
 * Cube squash & stretch — the cube reads as a springy mass, not a rigid box.
 * Driven by a signed `edgeImpulse` (+1 = a landing splat, −1 = a launch pop,
 * decaying to 0) plus a continuous airborne stretch from the cube's vertical
 * speed. All cosmetic deformation of the cube DRAW only — the hitbox stays the
 * axis-aligned `playerSize` square on `cubeY`.
 */
/** Rate (per second) the signed edge impulse eases back to 0 after a jump / land. */
export const CUBE_EDGE_IMPULSE_DECAY = 5;
/** Landing splat at full impulse: vertical compression + horizontal widen. */
export const CUBE_LAND_SQUASH = 0.26;
export const CUBE_LAND_WIDEN = 0.18;
/** Launch pop at full impulse: vertical stretch + horizontal thin (the spring releasing). */
export const CUBE_JUMP_STRETCH = 0.2;
export const CUBE_JUMP_THIN = 0.12;
/**
 * Airborne velocity stretch, peaking at full vertical speed (|vy| ≈ the jump
 * launch speed): the faster the cube travels, the more it elongates and thins,
 * so a quick rise / plummet reads dynamic while the apex hang reads neutral.
 * Applied in the cube's own (tumbling) frame; skipped under reduced motion.
 */
export const CUBE_AIR_STRETCH = 0.2;
export const CUBE_AIR_THIN = 0.12;
/** Safety rail clamping the composed cube scale so cranked feel-dial constants can't invert or balloon the sprite. */
export const CUBE_SCALE_MIN = 0.62;
export const CUBE_SCALE_MAX = 1.5;

/**
 * Press swell — while the button is held the cube grows slightly so a tap gives
 * instant tactile feedback. Crucially it also fires during the count-in (the
 * engine ignores those presses, but `isPressing()` still tracks them), so
 * spam-clicking before the run starts visibly responds. Eased toward the held
 * state each frame so it never snaps; applied as a uniform multiplier on the
 * cube draw, planted at its base so it grows up off the floor. Render-only.
 */
/** Extra uniform cube scale at a full press (fraction above 1). */
export const CUBE_PRESS_SCALE = 0.1;
/** Exponential approach rate (per second) the press swell eases toward its held target. */
export const CUBE_PRESS_PULSE_RATE = 22;

/**
 * Landing ground ripple — a flattened shockwave that spreads ALONG the floor
 * (not a radial ring), so a touchdown transfers visible weight into the ground.
 * Reuses the shockwave pool with a vertical squash; auto-skipped under reduced
 * motion like every shockwave. Kept brief + faint because auto-hop lands often.
 */
export const LAND_RIPPLE_END_R = 124;
export const LAND_RIPPLE_LIFE = 0.34;
export const LAND_RIPPLE_ALPHA = 0.4;
/** Vertical squash of a ground-ripple ellipse (1 = a normal circular ring). */
export const GROUND_RIPPLE_FLATTEN = 0.22;

/**
 * "In the zone" combo escalation. A render-only `comboEnergy` (0..1) ramps with
 * the live combo and drives the cube glow, trail intensity/colour, ground glow,
 * and a background bloom — so a long streak visibly feels powerful. Purely
 * cosmetic: derived from `state.combo`, NEVER fed back into the engine.
 */
/** Combo at which the escalation saturates (energy = 1). */
export const COMBO_ENERGY_FULL_AT = 40;
/** Exponential approach rate (per second) of the smoothed energy toward its combo-derived target. */
export const COMBO_ENERGY_APPROACH_RATE = 3;
/** Extra cube glow alpha + halo-radius fraction added at full combo energy. */
export const COMBO_GLOW_ALPHA_BONUS = 0.42;
export const COMBO_GLOW_RADIUS_BONUS = 0.35;
/** Trail alpha + line-width gains, and how far its colour shifts toward white, at full energy. */
export const COMBO_TRAIL_ALPHA_BONUS = 0.4;
export const COMBO_TRAIL_WIDTH_BONUS = 3;
export const COMBO_TRAIL_WHITEN = 0.45;
/** Extra ground top-edge glow alpha at full combo energy. */
export const COMBO_GROUND_GLOW_BONUS = 0.3;
/** Background combo bloom: peak alpha at full energy, and the energy below which it doesn't draw. */
export const COMBO_BLOOM_MAX_ALPHA = 0.16;
export const COMBO_BLOOM_MIN_ENERGY = 0.12;
/** Bloom radius as a fraction of the canvas's larger dimension. */
export const COMBO_BLOOM_RADIUS_FRAC = 0.62;

/** Corner vignette: base darkening (gated on `decorations`) + extra at full combo energy. */
export const VIGNETTE_BASE_ALPHA = 0.26;
export const VIGNETTE_COMBO_ALPHA_BONUS = 0.12;

/**
 * "In the zone" speed lines — horizontal motion streaks that rush past behind
 * the obstacles once a streak pushes `comboEnergy` past the threshold, so a hot
 * run literally feels faster. Beat-locked drift (deterministic with the scroll),
 * additive + faint so hazards stay readable. Another render-only `comboEnergy`
 * channel; skipped under reduced motion and on the low tier (`decorations`).
 */
export const SPEED_LINES_MIN_ENERGY = 0.4;
export const SPEED_LINES_COUNT = 14;
export const SPEED_LINES_MAX_ALPHA = 0.2;
/** Streak length as a fraction of canvas width at full energy. */
export const SPEED_LINES_LENGTH_FRAC = 0.16;
/** Streak forward drift as a fraction of canvas width per beat (well above the star drift). */
export const SPEED_LINES_DRIFT_FRAC_PER_BEAT = 0.6;

/**
 * Obstacle anticipation telegraph. As an upcoming obstacle scrolls within this
 * many beats of the cube's column it gets a pulsing accent-coloured outline +
 * a small bobbing chevron, so clearing it feels earned. Cosmetic, beat-synced,
 * skipped under reduced motion.
 */
export const OBSTACLE_TELEGRAPH_BEATS = 2.2;
/** Max telegraph outline/chevron alpha at closest approach. */
export const OBSTACLE_TELEGRAPH_MAX_ALPHA = 0.6;
/** Telegraph pulse cycles per beat. */
export const OBSTACLE_TELEGRAPH_PULSE_PER_BEAT = 2;
/**
 * Chevron geometry, in reference px (scaled by the layout like every obstacle so
 * the cue stays proportional at any window size): base half-width, point height,
 * resting gap above the obstacle top, and the bob oscillation amplitude.
 */
export const OBSTACLE_TELEGRAPH_HALF_WIDTH = 8;
export const OBSTACLE_TELEGRAPH_HEIGHT = 9;
export const OBSTACLE_TELEGRAPH_BOB_BASE = 8;
export const OBSTACLE_TELEGRAPH_BOB_AMP = 6;

/**
 * Auto-hop "hold aura": while the button is held the cube sheds a faint stream
 * of accent embers so holding (auto-hop armed) feels charged and responsive
 * rather than passive. Emitted every N frames (sparser on the low tier);
 * skipped under reduced motion and during hit-stop.
 */
export const HOLD_EMBER_INTERVAL_FRAMES = 3;
export const HOLD_EMBER_INTERVAL_FRAMES_LOW = 5;
export const HOLD_EMBER_COUNT = 2;

/**
 * Death shatter — the cube breaks into a grid of shards flung outward, drawn as
 * a SINGLE frozen "exploded" pose on the death frame (the rAF loop stops one
 * frame after death, so this must read instantly, never as a decaying
 * animation). Painted over the death overlay so it isn't swallowed by the flash.
 */
/** Shards per axis (3 → a 3×3 grid = 9 shards). */
export const DEATH_SHARD_GRID = 3;
/** How far the rim shards fling out, as a fraction of the cube half-size. */
export const DEATH_SHARD_EXPLODE = 1.05;
/** Inset shrinking each shard so the fracture gaps between them read. */
export const DEATH_SHARD_GAP = 0.16;
/** Max extra rotation (radians) a shard tumbles by in the frozen pose. */
export const DEATH_SHARD_SPIN = 0.6;
